import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/Button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Progress } from '../ui/progress';
import { 
  Building2,
  Users,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  TrendingUp,
  Calendar,
  FileText,
  MessageSquare,
  Phone,
  Video,
  MapPin,
  Settings,
  Bell,
  Search,
  Filter,
  Download,
  Upload,
  Edit,
  Eye,
  Plus,
  BarChart3,
  PieChart,
  Target,
  Award,
  Stethoscope,
  CalendarDays,
  ClipboardList,
  UserCheck,
  Mail,
  Star,
  Activity,
  DollarSign,
  FileCheck,
  UserPlus,
  CalendarClock
} from 'lucide-react';

// Mock data for facility dashboard
const mockReferralHistory = [
  {
    id: 'REF-001',
    patientName: 'Margaret Thompson',
    referralType: 'Psychiatric Evaluation',
    urgency: 'urgent',
    dateSubmitted: '2024-01-20',
    status: 'accepted',
    assignedProvider: 'Dr. Sarah Smith',
    scheduledDate: '2024-01-25',
    estimatedCompletion: '2024-01-25'
  },
  {
    id: 'REF-002',
    patientName: 'Robert Chen',
    referralType: 'Medication Management',
    urgency: 'routine',
    dateSubmitted: '2024-01-18',
    status: 'completed',
    assignedProvider: 'Dr. Sarah Smith',
    scheduledDate: '2024-01-22',
    completedDate: '2024-01-22'
  },
  {
    id: 'REF-003',
    patientName: 'Eleanor Williams',
    referralType: 'Crisis Intervention',
    urgency: 'emergent',
    dateSubmitted: '2024-01-15',
    status: 'completed',
    assignedProvider: 'Dr. Michael Johnson',
    scheduledDate: '2024-01-15',
    completedDate: '2024-01-15'
  },
  {
    id: 'REF-004',
    patientName: 'James Wilson',
    referralType: 'Follow-up Evaluation',
    urgency: 'routine',
    dateSubmitted: '2024-01-14',
    status: 'pending',
    assignedProvider: null,
    scheduledDate: null
  }
];

const mockUpcomingVisits = [
  {
    id: 'VISIT-001',
    providerName: 'Dr. Sarah Smith',
    patientName: 'Margaret Thompson',
    type: 'Psychiatric Evaluation',
    date: '2024-01-25',
    time: '10:00 AM',
    duration: 60,
    mode: 'onsite',
    status: 'confirmed'
  },
  {
    id: 'VISIT-002',
    providerName: 'Dr. Michael Johnson',
    patientName: 'Dorothy Martinez',
    type: 'Medication Review',
    date: '2024-01-26',
    time: '2:00 PM',
    duration: 45,
    mode: 'telehealth',
    status: 'confirmed'
  }
];

const mockStaffMessages = [
  {
    id: 'MSG-001',
    from: 'Dr. Sarah Smith',
    subject: 'Margaret Thompson - Post-Evaluation Update',
    message: 'Evaluation completed successfully. Medication adjustment recommended. Full report uploaded.',
    timestamp: '2024-01-23 3:15 PM',
    unread: true,
    priority: 'normal'
  },
  {
    id: 'MSG-002',
    from: 'Dr. Michael Johnson',
    subject: 'Schedule Change Request',
    message: 'Need to reschedule tomorrow\'s visit to 3:00 PM due to emergency. Please confirm.',
    timestamp: '2024-01-23 11:30 AM',
    unread: true,
    priority: 'high'
  }
];

export const FacilityDashboard = () => {
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-amber-100 text-amber-800';
      case 'accepted': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'declined': return 'bg-red-100 text-red-800';
      default: return 'bg-clinic-gray-100 text-clinic-gray-800';
    }
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'emergent': return 'bg-red-100 text-red-800';
      case 'urgent': return 'bg-amber-100 text-amber-800';
      case 'routine': return 'bg-green-100 text-green-800';
      default: return 'bg-clinic-gray-100 text-clinic-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <AlertCircle className="w-4 h-4" />;
      case 'accepted': return <CheckCircle className="w-4 h-4" />;
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'declined': return <XCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const filteredReferrals = mockReferralHistory.filter(referral => {
    const matchesStatus = filterStatus === 'all' || referral.status === filterStatus;
    const matchesSearch = referral.patientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         referral.referralType.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-clinic-blue-50 to-clinic-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold text-clinic-gray-900 mb-2">
                Facility Dashboard
              </h1>
              <p className="text-lg text-clinic-gray-600">
                Sunny Meadows Assisted Living
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <Button variant="outline" className="btn-md">
                <Bell className="w-4 h-4 mr-2" />
                Notifications (2)
              </Button>
              <Button className="btn-md bg-clinic-blue-600 hover:bg-clinic-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                New Referral
              </Button>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card className="card-elevated">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-clinic-gray-600 mb-1">Active Referrals</p>
                    <p className="text-3xl font-bold text-clinic-gray-900">3</p>
                    <p className="text-xs text-blue-600 flex items-center mt-1">
                      <TrendingUp className="w-3 h-3 mr-1" />
                      1 pending review
                    </p>
                  </div>
                  <div className="icon-container icon-container-lg icon-medical">
                    <ClipboardList className="icon-xl" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card-elevated">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-clinic-gray-600 mb-1">Upcoming Visits</p>
                    <p className="text-3xl font-bold text-clinic-gray-900">2</p>
                    <p className="text-xs text-green-600 flex items-center mt-1">
                      <Calendar className="w-3 h-3 mr-1" />
                      This week
                    </p>
                  </div>
                  <div className="icon-container icon-container-lg icon-success">
                    <CalendarDays className="icon-xl" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card-elevated">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-clinic-gray-600 mb-1">This Month</p>
                    <p className="text-3xl font-bold text-clinic-gray-900">12</p>
                    <p className="text-xs text-green-600 flex items-center mt-1">
                      <TrendingUp className="w-3 h-3 mr-1" />
                      8 completed
                    </p>
                  </div>
                  <div className="icon-container icon-container-lg icon-success">
                    <BarChart3 className="icon-xl" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card-elevated">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-clinic-gray-600 mb-1">Response Time</p>
                    <p className="text-3xl font-bold text-clinic-gray-900">4.2h</p>
                    <p className="text-xs text-green-600 flex items-center mt-1">
                      <Star className="w-3 h-3 mr-1" />
                      Average response
                    </p>
                  </div>
                  <div className="icon-container icon-container-lg icon-success">
                    <Clock className="icon-xl" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="referrals" className="space-y-6">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="referrals">Referrals</TabsTrigger>
            <TabsTrigger value="schedule">Schedule</TabsTrigger>
            <TabsTrigger value="residents">Residents</TabsTrigger>
            <TabsTrigger value="communications">Messages</TabsTrigger>
            <TabsTrigger value="reports">Reports</TabsTrigger>
          </TabsList>

          {/* Referrals Tab */}
          <TabsContent value="referrals" className="space-y-6">
            <Card className="card-elevated">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-3">
                      <ClipboardList className="icon-lg text-clinic-blue-600" />
                      Referral Status Tracking
                    </CardTitle>
                    <CardDescription>
                      Track the status of all submitted referrals and requests
                    </CardDescription>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-clinic-gray-400 w-4 h-4" />
                      <Input
                        placeholder="Search referrals..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 w-64"
                      />
                    </div>
                    <Button className="btn-md bg-clinic-blue-600 hover:bg-clinic-blue-700">
                      <Plus className="w-4 h-4 mr-2" />
                      New Referral
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {filteredReferrals.map((referral) => (
                    <Card key={referral.id} className="border-l-4 border-l-clinic-blue-500">
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-lg font-semibold text-clinic-gray-900">
                                {referral.patientName}
                              </h3>
                              <Badge className={getUrgencyColor(referral.urgency)}>
                                {referral.urgency}
                              </Badge>
                              <Badge className={getStatusColor(referral.status)}>
                                <div className="flex items-center gap-1">
                                  {getStatusIcon(referral.status)}
                                  {referral.status}
                                </div>
                              </Badge>
                            </div>
                            <div className="grid md:grid-cols-2 gap-4 text-sm text-clinic-gray-600">
                              <div className="flex items-center gap-2">
                                <Stethoscope className="w-4 h-4" />
                                {referral.referralType}
                              </div>
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4" />
                                Submitted: {referral.dateSubmitted}
                              </div>
                              {referral.assignedProvider && (
                                <div className="flex items-center gap-2">
                                  <UserCheck className="w-4 h-4" />
                                  Assigned: {referral.assignedProvider}
                                </div>
                              )}
                              {referral.scheduledDate && (
                                <div className="flex items-center gap-2">
                                  <CalendarDays className="w-4 h-4" />
                                  Scheduled: {referral.scheduledDate}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Button variant="outline" className="btn-sm">
                              <Eye className="w-4 h-4 mr-1" />
                              View Details
                            </Button>
                            {referral.status === 'completed' && (
                              <Button variant="outline" className="btn-sm">
                                <Download className="w-4 h-4 mr-1" />
                                Report
                              </Button>
                            )}
                            {referral.assignedProvider && referral.status !== 'completed' && (
                              <Button variant="outline" className="btn-sm">
                                <MessageSquare className="w-4 h-4 mr-1" />
                                Contact Provider
                              </Button>
                            )}
                          </div>
                        </div>

                        {/* Progress Timeline */}
                        <div className="mt-4 pt-4 border-t border-clinic-gray-200">
                          <div className="flex items-center justify-between text-xs text-clinic-gray-500 mb-2">
                            <span>Submitted</span>
                            <span>Under Review</span>
                            <span>Scheduled</span>
                            <span>Completed</span>
                          </div>
                          <Progress 
                            value={
                              referral.status === 'pending' ? 25 :
                              referral.status === 'accepted' ? 50 :
                              referral.scheduledDate ? 75 :
                              referral.status === 'completed' ? 100 : 25
                            } 
                            className="h-2" 
                          />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Schedule Tab */}
          <TabsContent value="schedule" className="space-y-6">
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <Card className="card-elevated">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3">
                      <CalendarDays className="icon-lg text-clinic-blue-600" />
                      Upcoming Provider Visits
                    </CardTitle>
                    <CardDescription>Scheduled appointments and consultations</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {mockUpcomingVisits.map((visit) => (
                      <Card key={visit.id} className="border-l-4 border-l-clinic-blue-500">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h3 className="font-semibold text-clinic-gray-900">
                                  {visit.providerName}
                                </h3>
                                <Badge className={visit.mode === 'telehealth' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}>
                                  {visit.mode === 'telehealth' ? (
                                    <Video className="w-3 h-3 mr-1" />
                                  ) : (
                                    <MapPin className="w-3 h-3 mr-1" />
                                  )}
                                  {visit.mode}
                                </Badge>
                              </div>
                              <div className="grid md:grid-cols-2 gap-2 text-sm text-clinic-gray-600">
                                <div className="flex items-center gap-2">
                                  <UserCheck className="w-4 h-4" />
                                  Patient: {visit.patientName}
                                </div>
                                <div className="flex items-center gap-2">
                                  <Stethoscope className="w-4 h-4" />
                                  {visit.type}
                                </div>
                                <div className="flex items-center gap-2">
                                  <Clock className="w-4 h-4" />
                                  {visit.date} at {visit.time}
                                </div>
                                <div className="flex items-center gap-2">
                                  <Activity className="w-4 h-4" />
                                  Duration: {visit.duration} minutes
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Button variant="outline" className="btn-sm">
                                <Edit className="w-4 h-4 mr-1" />
                                Reschedule
                              </Button>
                              <Button variant="outline" className="btn-sm">
                                <MessageSquare className="w-4 h-4 mr-1" />
                                Contact
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-6">
                <Card className="card-elevated">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3">
                      <Target className="icon-lg text-clinic-gold-600" />
                      Quick Actions
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Button className="w-full btn-md bg-clinic-blue-600 hover:bg-clinic-blue-700">
                      <Plus className="w-4 h-4 mr-2" />
                      Schedule Visit
                    </Button>
                    <Button variant="outline" className="w-full btn-md">
                      <CalendarClock className="w-4 h-4 mr-2" />
                      Request Locum Tenens
                    </Button>
                    <Button variant="outline" className="w-full btn-md">
                      <Calendar className="w-4 h-4 mr-2" />
                      View Full Calendar
                    </Button>
                  </CardContent>
                </Card>

                <Card className="card-elevated">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3">
                      <BarChart3 className="icon-lg text-clinic-success-600" />
                      This Month
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Completed Visits</span>
                        <span>8/12</span>
                      </div>
                      <Progress value={67} className="h-2" />
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Pending Referrals</span>
                        <span>3</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Residents Tab */}
          <TabsContent value="residents" className="space-y-6">
            <Card className="card-elevated">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <Users className="icon-lg text-clinic-blue-600" />
                  Resident Mental Health Overview
                </CardTitle>
                <CardDescription>
                  Mental health status and care coordination for facility residents
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12">
                  <div className="icon-container icon-container-2xl icon-medical mx-auto mb-4">
                    <Users className="icon-3xl" />
                  </div>
                  <h3 className="text-xl font-semibold text-clinic-gray-900 mb-2">
                    Resident Management Coming Soon
                  </h3>
                  <p className="text-clinic-gray-600 max-w-md mx-auto">
                    Comprehensive resident mental health tracking, care plans, and medication management will be available here.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Communications Tab */}
          <TabsContent value="communications" className="space-y-6">
            <Card className="card-elevated">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <MessageSquare className="icon-lg text-clinic-blue-600" />
                  Provider Communications
                </CardTitle>
                <CardDescription>
                  Messages and updates from psychiatric providers
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {mockStaffMessages.map((message) => (
                  <Card key={message.id} className={`border-l-4 ${message.unread ? 'border-l-clinic-blue-500 bg-clinic-blue-50' : 'border-l-clinic-gray-300'}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-clinic-gray-900">
                            {message.from}
                          </h3>
                          {message.unread && (
                            <Badge className="bg-clinic-blue-100 text-clinic-blue-800">
                              New
                            </Badge>
                          )}
                          {message.priority === 'high' && (
                            <Badge className="bg-red-100 text-red-800">
                              High Priority
                            </Badge>
                          )}
                        </div>
                        <span className="text-xs text-clinic-gray-500">{message.timestamp}</span>
                      </div>
                      <h4 className="font-medium text-clinic-gray-800 mb-2">{message.subject}</h4>
                      <p className="text-clinic-gray-700 mb-3">{message.message}</p>
                      <div className="flex items-center space-x-2">
                        <Button variant="outline" className="btn-sm">
                          <Mail className="w-4 h-4 mr-1" />
                          Reply
                        </Button>
                        <Button variant="outline" className="btn-sm">
                          <Phone className="w-4 h-4 mr-1" />
                          Call
                        </Button>
                        {message.priority === 'high' && (
                          <Button className="btn-sm bg-red-600 hover:bg-red-700 text-white">
                            <AlertCircle className="w-4 h-4 mr-1" />
                            Urgent Response
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports" className="space-y-6">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Card className="card-elevated">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <BarChart3 className="icon-lg text-clinic-blue-600" />
                    Service Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-clinic-gray-600">Total Referrals</span>
                      <span className="font-semibold">23</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-clinic-gray-600">Completed Services</span>
                      <span className="font-semibold">18</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-clinic-gray-600">Average Response Time</span>
                      <span className="font-semibold">4.2 hours</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-clinic-gray-600">Satisfaction Score</span>
                      <span className="font-semibold">4.8/5</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-elevated">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <PieChart className="icon-lg text-clinic-gold-600" />
                    Service Types
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Evaluations</span>
                      <span className="text-sm font-medium">40%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Medication Management</span>
                      <span className="text-sm font-medium">35%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Crisis Intervention</span>
                      <span className="text-sm font-medium">25%</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-elevated">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <FileText className="icon-lg text-clinic-success-600" />
                    Export Reports
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button variant="outline" className="w-full btn-md">
                    <Download className="w-4 h-4 mr-2" />
                    Monthly Summary
                  </Button>
                  <Button variant="outline" className="w-full btn-md">
                    <Download className="w-4 h-4 mr-2" />
                    Referral History
                  </Button>
                  <Button variant="outline" className="w-full btn-md">
                    <Download className="w-4 h-4 mr-2" />
                    Provider Reports
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};